/**
 * Best-effort copy-to-clipboard.
 *
 * - Uses the async Clipboard API when the tab is secure + focused.
 * - Falls back to a legacy `document.execCommand('copy')` off a
 *   synthetic `<textarea>` when the API is missing or rejects (older
 *   Safari, permission-blocked iframes).
 * - Never throws.
 * - Returns `true` on success, `false` on any failure.
 *
 * Callers surface success / failure via the app's toast system — the
 * user never sees a stack trace or a permission-denied error.
 *
 * Lives in its own module so tests can mock it wholesale rather than
 * fight jsdom's `navigator.clipboard` shape.
 */
export async function copyToClipboard(text) {
  const value = String(text ?? '');
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {
    // Fall through to the legacy path.
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = value;
    ta.setAttribute('readonly', '');
    // Off-screen but still focusable so `execCommand('copy')` can
    // grab the selection.
    ta.style.position = 'fixed';
    ta.style.top = '-1000px';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    // eslint-disable-next-line deprecation/deprecation
    const ok = document.execCommand?.('copy') === true;
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}
